<template>
  <div class="h-full essay">
    <teleport to="head">
      <title>{{ title }}</title>
    </teleport>
    <transition name="loadEssay">
      <div
        v-if="showContent"
        id="essay-content"
        v-html="essayContent"
        v-katex
      ></div>
    </transition>
    <transition name="loadEssay">
      <div id="toTop" class="mb-8" v-if="showContent">
        <button title="Go to top" class="block w-8 mx-auto text-3xl" @click="scrollToTop">▲</button>
      </div>
    </transition>
    <transition name="loadSub">
      <iframe
        v-if="showSub"
        class="container w-5/6 p-4 mx-auto mb-8 bg-white border-t border-gray-100 shadow-xl h-72 dark:shadow-3lg-dark sm:h-96 rounded-3xl bg-opacity-80"
        :src="iframeSrc"
        frameborder="0"
        scrolling="no"
        @load="showSub = true"
      ></iframe>
    </transition>
    <div class="h-8"></div>
    <PeekBox
      @overPeekBox="isInPeekBox = true"
      @leavePeekBox="
        isInPeekBox = false;
        handleLeaveRefLink();
      "
      :showPeekBox="showPeekBox"
      :eventClientX="eventClientX"
      :eventClientY="eventClientY"
      :isInPeekBox="isInPeekBox"
      :boxContent="refContent"
      :width="18"
      :height="8"
    />
  </div>
</template>

<script lang="ts">
import { Options, Vue } from "vue-class-component";
import axios from "axios";
import PeekBox from "@/components/PeekBox.vue";
import hljs from "highlight.js";

const smallWindow = window.matchMedia("(max-width: 640px)");

@Options({
  data() {
    return {
      title: "透明盒子计划·文章",
      iframeSrc: "",
      essayContent: "",
      showContent: false,
      showSub: false,
      refContent: "",
      showPeekBox: false,
      isInPeekBox: false,
      timer: "",
      eventClientX: 0,
      eventClientY: 0
    };
  },
  components: {
    PeekBox
  },
  watch: {
    $route(to, from) {
      this.showContent = false;
      this.showSub = false;
      if (to.name !== "Essays") return;
      this.fetchEssay(to.params.essayName);
      this.addClickListener();
      this.scrollToTop();
      this.highlightCode();
      this.updateTitle();
      this.showSubForm();
    }
  },
  mounted() {
    this.fetchEssay(this.$route.params.essayName);
    this.addClickListener();
    this.scrollToTop();
    this.highlightCode();
    this.updateTitle();
    this.showSubForm();
  },
  beforeUnmount() {
    this.removeClickListener();
  },
  methods: {
    showSubForm() {
      this.iframeSrc = "https://clearbox.substack.com/embed";
      setTimeout(() => {
        this.showSub = true;
      }, 800);
    },
    addClickListener() {
      setTimeout(() => {
        let els = this.$el.getElementsByTagName("a");
        els.forEach(el => el.addEventListener("click", this.handleClick));

        els = this.$el.getElementsByClassName("footnote-ref");
        els.forEach(el => {
          el.addEventListener("mouseenter", this.handleOverRefLink);
          el.addEventListener("mouseleave", this.handleLeaveRefLink);
        });
      }, 600);
    },
    removeClickListener() {
      let els = this.$el.getElementsByTagName("a");
      els.forEach(el => el.removeEventListener("click", this.handleClick));

      els = this.$el.getElementsByClassName("footnote-ref");
      els.forEach(el => {
        el.removeEventListener("mouseenter", this.handleOverRefLink);
        el.removeEventListener("mouseleave", this.handleLeaveRefLink);
      });
    },
    highlightCode() {
      hljs.configure({
        tabReplace: "  ",
        languages: ["javascript", "python", "ruby", "swift", "c"]
      });
      setTimeout(() => {
        this.$el.getElementsByTagName("code").forEach(block => {
          hljs.highlightBlock(block);
        });
      }, 600);
    },
    scrollToTop() {
      setTimeout(() => {
        document.getElementById("app")?.scrollIntoView({
          behavior: "smooth",
          block: "start"
        });
      }, 100);
    },
    updateTitle() {
      setTimeout(() => {
        const el = this.$el.getElementsByClassName("note-title")[0];
        if (el) {
          this.title = `透明盒子计划「${el.innerText}」`;
        } else {
          this.title = "透明盒子计划·文章";
        }
      }, 600);
    },
    handleOverRefLink(event) {
      if (smallWindow.matches) return;
      this.timer = setTimeout(() => {
        this.eventClientX = event.clientX;
        this.eventClientY = event.clientY;

        const refLink = event.target.getElementsByTagName("a")[0];
        const refId = refLink.getAttribute("href").split("#")[1];
        const ref = document.getElementById(refId);
        if (ref) {
          this.refContent = ref.innerHTML.replace("↩", "");
          this.showPeekBox = true;
        }
      }, 1000);
    },
    handleLeaveRefLink(event) {
      if (smallWindow.matches) return;
      clearTimeout(this.timer);
      setTimeout(() => {
        if (!this.isInPeekBox) this.showPeekBox = false;
      }, 200);
    },
    fetchEssay(path: string) {
      axios
        .get(`/notes/essays/${path}`)
        .then(res => {
          this.essayContent = res.data;
          this.showContent = true;
        })
        .catch(err => {
          console.log(err);
        });
    },
    scrollToElement(el) {
      if (el) {
        el.scrollIntoView({
          behavior: "smooth",
          block: "center",
          inline: "center"
        });
        el.classList.add("highlight");
        setTimeout(() => {
          el.classList.remove("highlight");
        }, 5000);
      }
    },
    handleClick(event) {
      const { target } = event;
      const href = target.getAttribute("href");
      if (href.match(/^#fn-\d+$/) || href.match(/^#fnref-\d+$/)) {
        event.preventDefault();
        const el = document.getElementById(href.split("#")[1]);
        this.scrollToElement(el);
        return;
      }
      const to = this.filterClickEvent(event);
      if (to) {
        event.preventDefault();
        this.removeClickListener();
        this.showSub = false;
        this.showContent = false;
        this.$router.push({
          path: to
        });
      }
    },
    filterClickEvent(event) {
      // ensure we use the link, in case the click has been received by a subelement
      let { target } = event;
      while (target && target.tagName !== "A") target = target.parentNode;
      // handle only links that occur inside the component and do not reference external resources
      if (
        target &&
        target.matches("#essay-content a:not([href*='://'])") &&
        target.href
      ) {
        // some sanity checks taken from vue-router:
        // https://github.com/vuejs/vue-router/blob/dev/src/components/link.js#L106
        const {
          altKey,
          ctrlKey,
          metaKey,
          shiftKey,
          button,
          defaultPrevented
        } = event;
        // don't handle with control keys
        if (metaKey || altKey || ctrlKey || shiftKey) return false;
        // don't handle when preventDefault called
        // if (defaultPrevented) return
        // don't handle right clicks
        if (button !== undefined && button !== 0) return false;
        // don't handle if `target="_blank"`
        if (target && target.getAttribute) {
          const linkTarget = target.getAttribute("target");
          if (/\b_blank\b/i.test(linkTarget)) return false;
        }
        // don't handle same page links/anchors
        const url = new URL(target.href);
        const to = url.pathname.split("/")[1];
        if (event.preventDefault) {
          return to;
        } else {
          return false;
        }
      }
    }
  }
})
export default class Essay extends Vue {}
</script>
